import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell as RCell } from "recharts";
import { TrendingUp, FileCheck2, ShieldX, Coins, Clock, FileWarning } from "lucide-react";

const COLORS = ["#1E3A8A", "#10B981", "#F59E0B", "#6366F1", "#EF4444", "#06B6D4"];

export default function AdminDashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/dashboard/admin").then((r) => setS(r.data)); }, []);
  if (!s) return <Layout><div className="text-slate-500">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="fade-in space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>Admin overview</h1>
          <p className="text-slate-600 text-sm mt-1">Portfolio health, throughput, and operational backlog.</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI icon={TrendingUp} label="Total applications" value={s.total_applications} testId="kpi-total-apps"/>
          <KPI icon={Clock} label="Pending review" value={s.pending_review} testId="kpi-pending"/>
          <KPI icon={FileCheck2} label="Approved loans" value={s.approved_loans} testId="kpi-approved"/>
          <KPI icon={ShieldX} label="Rejected" value={s.rejected_loans} testId="kpi-rejected"/>
          <KPI icon={Coins} label="Principal approved" value={`$${s.total_principal_approved.toLocaleString()}`} testId="kpi-principal"/>
          <KPI icon={Coins} label="Total disbursed" value={`$${s.total_disbursed.toLocaleString()}`} testId="kpi-disbursed"/>
          <KPI icon={TrendingUp} label="Approval rate" value={`${s.approval_rate}%`} testId="kpi-approval-rate"/>
          <KPI icon={FileWarning} label="Docs pending" value={s.documents_pending} testId="kpi-docs-pending"/>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="rounded-xl border-slate-200"><CardContent className="p-6">
            <h3 className="font-semibold text-slate-900" style={{fontFamily:'Outfit'}}>Applications by product</h3>
            <div className="h-64 mt-4" style={{minHeight: 240}} data-testid="chart-by-product">
              <ResponsiveContainer width="100%" height="100%" minHeight={240}>
                <BarChart data={s.applications_by_product}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-12} textAnchor="end" height={50}/>
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false}/>
                  <Tooltip/>
                  <Bar dataKey="count" radius={[6,6,0,0]} fill="#1E3A8A"/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent></Card>
          <Card className="rounded-xl border-slate-200"><CardContent className="p-6">
            <h3 className="font-semibold text-slate-900" style={{fontFamily:'Outfit'}}>Mix of outcomes</h3>
            <div className="h-64 mt-4" style={{minHeight: 240}} data-testid="chart-outcomes">
              <ResponsiveContainer width="100%" height="100%" minHeight={240}>
                <PieChart>
                  <Tooltip/>
                  <Pie
                    data={[
                      {name:"Approved", value: s.approved_loans},
                      {name:"Rejected", value: s.rejected_loans},
                      {name:"Pending", value: s.pending_review},
                    ]}
                    dataKey="value" nameKey="name"
                    outerRadius={80} label
                  >
                    {[0,1,2].map((i) => <RCell key={i} fill={COLORS[i]}/>)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent></Card>
        </div>
      </div>
    </Layout>
  );
}

function KPI({ icon: Icon, label, value, testId }) {
  return (
    <Card className="rounded-xl border-slate-200" data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
          <Icon className="h-4 w-4 text-slate-400"/>
        </div>
        <div className="mt-1 text-2xl font-semibold text-slate-900" style={{fontFamily:'Outfit'}}>{value}</div>
      </CardContent>
    </Card>
  );
}
